import { ArrowRight, Loader2, CheckCircle2, AlertCircle, X } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Header } from './Header';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL,
  import.meta.env.VITE_SUPABASE_ANON_KEY
);

interface HomePageProps {
  onOpenChat: (message?: string) => void;
}

interface RegistrationData {
  name: string;
  email: string;
  organization: string;
  role: string;
  phone: string;
  help_description: string;
}

const featuredWork = [
  {
    id: 'ewn-agent',
    name: 'EWN Agent',
    blurb: 'Detects delays, drafts contract-ready Early Warning Notices.',
    href: '/movar-tools',
  },
  {
    id: 'data-mapper',
    name: 'Data Mapper',
    blurb: 'Semantic mapping between unrelated tables. Cost codes, activities, references.',
    href: '/movar-tools',
  },
  {
    id: 'schedule-analyser',
    name: 'Schedule Analyser',
    blurb: 'XER in, 4-week look-ahead and a breakdown of every schedule table out.',
    href: '/movar-tools',
  },
];

const compassPrompts = [
  'Where have you proven ROI from AI on infrastructure programmes?',
  'What guardrails sit around agents that can write back to live systems?',
  'How does your delivery model differ from a generic data consultancy?',
  'How do you scope and price a programme-scale AI rollout?',
];

export function HomePage({ onOpenChat }: HomePageProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);
  const [formData, setFormData] = useState<RegistrationData>({
    name: '',
    email: '',
    organization: '',
    role: '',
    phone: '',
    help_description: '',
  });
  const [showForm, setShowForm] = useState(false);

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const { error } = await supabase.from('registrations').insert([formData]);
      if (error) throw error;

      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        organization: '',
        role: '',
        phone: '',
        help_description: '',
      });
      setTimeout(() => {
        setShowForm(false);
        setSubmitStatus(null);
      }, 2000);
    } catch (error) {
      console.error('Error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header showHomeButton={false} />

      {showForm && (
        <RegisterInterestDialog
          formData={formData}
          isSubmitting={isSubmitting}
          submitStatus={submitStatus}
          onChange={handleInputChange}
          onSubmit={handleFormSubmit}
          onClose={() => setShowForm(false)}
        />
      )}

      <main className="flex-1">
        {/* ─── HERO ────────────────────────────────────────────────── */}
        <section className="max-w-7xl mx-auto px-6 lg:px-12 pt-16 lg:pt-28 pb-28 lg:pb-36">
          <div className="grid grid-cols-12 gap-x-6">
            <div className="col-span-12 lg:col-span-10">
              <p className="eyebrow text-primary mb-8 fade-up" style={{ animationDelay: '0ms' }}>
                01 · What we do
              </p>

              <h1 className="display-xl text-white mb-10 fade-up" style={{ animationDelay: '80ms' }}>
                Sharper project outcomes,
                <br className="hidden md:block" /> engineered with data and AI agents.
              </h1>

              <p
                className="text-lg md:text-xl text-white/65 max-w-2xl mb-12 leading-snug fade-up"
                style={{ animationDelay: '180ms' }}
              >
                From schedule, cost and risk to autonomous agents that read your P6 and draft your
                Early Warning Notices. Engineered for defence, utilities, energy, and rail.
              </p>

              <div
                className="flex flex-wrap items-center gap-3 fade-up"
                style={{ animationDelay: '260ms' }}
              >
                <button onClick={() => setShowForm(true)} className="btn-primary">
                  Register interest
                  <ArrowRight className="w-4 h-4" />
                </button>
                <button onClick={() => onOpenChat()} className="btn-secondary">
                  Talk to Compass
                </button>
              </div>
            </div>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="hairline" />
        </div>

        {/* ─── PROOF ───────────────────────────────────────────────── */}
        <section className="max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-28">
          <div className="grid grid-cols-12 gap-x-6 mb-12 lg:mb-16">
            <div className="col-span-12 lg:col-span-3">
              <p className="eyebrow text-primary mb-3">02 · Proof</p>
            </div>
            <div className="col-span-12 lg:col-span-9">
              <h2 className="display-md text-white max-w-2xl mb-6">
                Recognised. Named. Delivered.
              </h2>
              <p className="text-base md:text-lg text-white/60 max-w-2xl leading-relaxed">
                <span className="text-primary font-semibold">PCE 2024 · Global Innovation of the Year.</span>{' '}
                Awarded by the Project Controls Expo for our work with Severn Trent Water on
                AI-driven project controls.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {[
              { name: 'EKFB', sub: 'HS2 Joint Venture' },
              { name: 'Severn Trent Water', sub: 'AMP project controls' },
              { name: 'Sellafield', sub: 'Nuclear decommissioning' },
              { name: 'South East Water', sub: 'AMP delivery' },
              { name: 'Ferrovial', sub: 'Major projects' },
              { name: 'And more', sub: 'Across the Reply group' },
            ].map((client, i) => (
              <div key={client.name} className="bg-[#131B30] p-6 md:p-8">
                <p className="eyebrow text-white/35 mb-2">{String(i + 1).padStart(2, '0')}</p>
                <p className="text-lg md:text-xl font-bold text-white leading-tight">{client.name}</p>
                <p className="text-sm text-white/45 mt-1">{client.sub}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 grid grid-cols-12 gap-x-6">
            <div className="col-span-12 lg:col-start-4 lg:col-span-9">
              <p className="text-sm text-white/45 max-w-2xl leading-relaxed">
                A boutique team backed by{' '}
                <span className="text-white/75">16,000+ specialists across the Reply group</span>.
                Senior delivery on the engagements that matter; the wider network on tap when scale
                demands it.
              </p>
            </div>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="hairline" />
        </div>

        {/* ─── COMPASS PROMPTS ─────────────────────────────────────── */}
        <section className="max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-28">
          <div className="grid grid-cols-12 gap-x-6 mb-10 lg:mb-14">
            <div className="col-span-12 lg:col-span-3">
              <p className="eyebrow text-primary mb-3">03 · Ask Compass</p>
            </div>
            <div className="col-span-12 lg:col-span-9">
              <h2 className="display-md text-white max-w-2xl">
                Our PMO consultant, answering the questions you'd usually pay a partner to ask.
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-12 gap-x-6">
            <div className="col-span-12 lg:col-start-4 lg:col-span-9">
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-px">
                {compassPrompts.map((prompt, i) => (
                  <li key={prompt}>
                    <button
                      onClick={() => onOpenChat(prompt)}
                      className="group w-full text-left flex items-start gap-4 py-5 border-t border-white/10 hover:border-primary transition-colors"
                    >
                      <span className="eyebrow text-white/40 pt-1 shrink-0">
                        {String(i + 1).padStart(2, '0')}
                      </span>
                      <span className="flex-1 text-base md:text-lg text-white/85 group-hover:text-white transition-colors leading-snug">
                        {prompt}
                      </span>
                      <ArrowRight className="w-4 h-4 text-white/30 group-hover:text-primary mt-1.5 shrink-0 transition-colors" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="hairline" />
        </div>

        {/* ─── IN PRACTICE ─────────────────────────────────────────── */}
        <section className="max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-28">
          <div className="grid grid-cols-12 gap-x-6 mb-12 lg:mb-16">
            <div className="col-span-12 lg:col-span-3">
              <p className="eyebrow text-primary mb-3">04 · In practice</p>
            </div>
            <div className="col-span-12 lg:col-span-9">
              <h2 className="display-md text-white max-w-2xl">
                Software shipping today on live programmes.
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-12 gap-x-6 gap-y-10">
            {featuredWork.map((item, i) => (
              <Link
                key={item.id}
                to={item.href}
                className="col-span-12 md:col-span-6 lg:col-span-4 group"
              >
                <div className="border-t border-white/10 group-hover:border-primary transition-colors pt-6">
                  <p className="eyebrow text-white/40 mb-3">
                    {String(i + 1).padStart(2, '0')} · Live
                  </p>
                  <h3 className="text-2xl font-bold text-white mb-2 group-hover:text-primary transition-colors leading-tight">
                    {item.name}
                  </h3>
                  <p className="text-white/60 leading-relaxed">{item.blurb}</p>
                </div>
              </Link>
            ))}
          </div>

          <div className="mt-14">
            <Link to="/movar-tools" className="link-arrow text-sm">
              See the full dev lab
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="hairline" />
        </div>

        {/* ─── HOW WE ENGAGE ───────────────────────────────────────── */}
        <section className="max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-28">
          <div className="grid grid-cols-12 gap-x-6 mb-12 lg:mb-16">
            <div className="col-span-12 lg:col-span-3">
              <p className="eyebrow text-primary mb-3">05 · How we engage</p>
            </div>
            <div className="col-span-12 lg:col-span-9">
              <h2 className="display-md text-white max-w-2xl">
                Three ways to work with us.
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-12 gap-x-6 gap-y-10">
            {[
              {
                phase: 'Discovery',
                duration: '2–6 weeks',
                detail: 'Diagnostic engagement. We map your project-controls data estate, identify the highest-value AI use cases, and return a costed roadmap.',
                fit: 'Sizing the opportunity before committing to delivery.',
              },
              {
                phase: 'Build',
                duration: '8–24 weeks',
                detail: 'Fixed-scope delivery of a defined capability: Fabric data platform, AI agent, integrated reporting, or domain analytics.',
                fit: 'A clear use case and an internal sponsor ready to deploy.',
              },
              {
                phase: 'Embedded',
                duration: 'Ongoing',
                detail: 'Senior team embedded into your project controls or transformation function. Continuous delivery, capability transfer, joint roadmap.',
                fit: 'Programme-scale clients who want a long-term partner.',
              },
            ].map((p, i) => (
              <div key={p.phase} className="col-span-12 md:col-span-4 group">
                <div className="border-t border-white/10 pt-6">
                  <div className="flex items-baseline justify-between mb-4">
                    <p className="eyebrow text-white/40">{String(i + 1).padStart(2, '0')}</p>
                    <p className="font-mono text-xs text-primary tracking-widest uppercase">
                      {p.duration}
                    </p>
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-3 leading-tight">
                    {p.phase}
                  </h3>
                  <p className="text-white/65 leading-relaxed mb-5">{p.detail}</p>
                  <p className="text-sm text-white/45 leading-relaxed">
                    <span className="text-white/65">Best for:</span> {p.fit}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-14">
            <Link to="/register" className="link-arrow text-sm">
              Start a conversation
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="hairline" />
        </div>

        {/* ─── STATEMENT ───────────────────────────────────────────── */}
        <section className="max-w-7xl mx-auto px-6 lg:px-12 py-28 lg:py-44">
          <div className="grid grid-cols-12 gap-x-6">
            <div className="col-span-12 lg:col-span-2">
              <p className="eyebrow text-primary">06 · Ethos</p>
            </div>
            <div className="col-span-12 lg:col-span-10">
              <p className="display-lg text-white leading-[1.04] tracking-tight">
                Big enough to <span className="text-primary">deliver</span>.
                <br />
                Small enough to <span className="text-primary">care</span>.
              </p>
              <p className="mt-8 text-base md:text-lg text-white/55 max-w-xl">
                A lean team of project-controls engineers and applied-AI specialists. We take the
                jobs that big consultancies won't sweat and the small ones won't risk.
              </p>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8 flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-8">
          <p className="eyebrow text-white/40">&copy; 2026 Movar Reply</p>
          <p className="text-sm text-white/45 italic">big enough to deliver, small enough to care</p>
          <p className="eyebrow text-white/30 md:ml-auto">A Reply Group company</p>
        </div>
      </footer>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────── */

interface DialogProps {
  formData: RegistrationData;
  isSubmitting: boolean;
  submitStatus: 'success' | 'error' | null;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onSubmit: (e: React.FormEvent) => void;
  onClose: () => void;
}

function RegisterInterestDialog({
  formData,
  isSubmitting,
  submitStatus,
  onChange,
  onSubmit,
  onClose,
}: DialogProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-md">
      <div className="midnight-card relative w-full max-w-lg">
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute top-4 right-4 p-2 text-white/50 hover:text-white transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="p-8 border-b border-white/10">
          <p className="eyebrow text-primary mb-2">Register interest</p>
          <h2 className="display-md text-white">Let's talk.</h2>
        </div>
        <form onSubmit={onSubmit} className="p-8 space-y-5">
          <Field label="Name">
            <input
              type="text"
              name="name"
              required
              value={formData.name}
              onChange={onChange}
              className="form-input"
              placeholder="Your full name"
            />
          </Field>
          <Field label="Work email">
            <input
              type="email"
              name="email"
              required
              value={formData.email}
              onChange={onChange}
              className="form-input"
              placeholder="you@company.com"
            />
          </Field>
          <Field label="Organisation">
            <input
              type="text"
              name="organization"
              required
              value={formData.organization}
              onChange={onChange}
              className="form-input"
              placeholder="Company"
            />
          </Field>
          <Field label="Role">
            <input
              type="text"
              name="role"
              required
              value={formData.role}
              onChange={onChange}
              className="form-input"
              placeholder="Head of PMO"
            />
          </Field>
          <Field label="Phone">
            <input
              type="tel"
              name="phone"
              required
              value={formData.phone}
              onChange={onChange}
              className="form-input"
              placeholder="+44 …"
            />
          </Field>
          <Field label="How can Movar Reply help?">
            <textarea
              name="help_description"
              required
              value={formData.help_description}
              onChange={onChange}
              rows={4}
              className="form-input resize-none"
              placeholder="Tell us about your programme, the problem, the timeline."
            />
          </Field>

          {submitStatus && (
            <div
              className={`p-4 flex items-center gap-3 border ${
                submitStatus === 'success'
                  ? 'border-green-500/30 text-green-400 bg-green-500/5'
                  : 'border-red-500/30 text-red-400 bg-red-500/5'
              }`}
            >
              {submitStatus === 'success' ? (
                <>
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                  <p className="text-sm">Thank you. We'll be in touch shortly.</p>
                </>
              ) : (
                <>
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <p className="text-sm">Something went wrong. Please try again.</p>
                </>
              )}
            </div>
          )}

          <div className="flex items-center justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancel
            </button>
            <button type="submit" disabled={isSubmitting} className="btn-primary disabled:opacity-50">
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Sending</span>
                </>
              ) : (
                <>
                  <span>Send</span>
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="eyebrow text-white/60 mb-2 block">{label}</span>
      {children}
    </label>
  );
}
